const trailingDigits = /(\d+)$/;
const trailingLetters = /([A-Za-z]+)$/;

const incrementNumericToken = (token: string): string => {
  const next = (BigInt(token) + 1n).toString();
  return next.padStart(token.length, "0");
};

const incrementAlphaToken = (token: string): string => {
  const wasUppercase = token === token.toUpperCase();
  const characters = token.toUpperCase().split("");

  let position = characters.length - 1;
  while (position >= 0 && characters[position] === "Z") {
    characters[position] = "A";
    position -= 1;
  }
  if (position < 0) {
    characters.unshift("A");
  } else {
    characters[position] = String.fromCharCode((characters[position] as string).charCodeAt(0) + 1);
  }

  const next = characters.join("");
  return wasUppercase ? next : next.toLowerCase();
};

export type PatternKind = "numeric" | "alpha" | "unknown";

export const detectPatternKind = (input: string): PatternKind => {
  if (trailingDigits.test(input)) return "numeric";
  if (trailingLetters.test(input)) return "alpha";
  return "unknown";
};

export const REVISION_SEQUENCE_TYPES = ["PATTERN", "ALPHA_NUMERIC_PREFIXED"] as const;
export type RevisionSequenceType = (typeof REVISION_SEQUENCE_TYPES)[number];

export const DEFAULT_SEQUENCE_TYPE: RevisionSequenceType = "PATTERN";
export const CUSTOM_SEQUENCE_TYPE: RevisionSequenceType = "ALPHA_NUMERIC_PREFIXED";
export const CUSTOM_SEQUENCE_START = "A";

export const isCustomSequenceType = (sequenceType?: RevisionSequenceType | null): boolean =>
  sequenceType === CUSTOM_SEQUENCE_TYPE;
export const resolveStartPattern = (startPattern: string, sequenceType?: RevisionSequenceType | null): string =>
  isCustomSequenceType(sequenceType) ? CUSTOM_SEQUENCE_START : startPattern.trim();

type RevisionScheme = {
  isMember: (value: string, startPattern: string) => boolean;
  next: (label: string) => string | null;
  toOrdinal: (label: string, startPattern: string) => number | null;
  fromOrdinal: (n: number, startPattern: string) => string | null;
};

const PATTERN_MAX_POSITIONS = 2000;

const patternNext = (current: string): string | null => {
  const numericMatch = current.match(trailingDigits);
  if (numericMatch && numericMatch[1]) {
    const token = numericMatch[1];
    return current.slice(0, -token.length) + incrementNumericToken(token);
  }
  const alphaMatch = current.match(trailingLetters);
  if (alphaMatch && alphaMatch[1]) {
    const token = alphaMatch[1];
    return current.slice(0, -token.length) + incrementAlphaToken(token);
  }
  return null;
};

const patternIsMember = (value: string, startPattern: string): boolean => {
  if (!value || !startPattern) return false;
  const splitTrailing = (s: string) => {
    const num = s.match(trailingDigits);
    if (num?.[1]) return { prefix: s.slice(0, -num[1].length), kind: "numeric" as const };
    const alpha = s.match(trailingLetters);
    if (alpha?.[1]) return { prefix: s.slice(0, -alpha[1].length), kind: "alpha" as const };
    return null;
  };
  const v = splitTrailing(value);
  const s = splitTrailing(startPattern);
  return !!v && !!s && v.kind === s.kind && v.prefix === s.prefix;
};

const patternScheme: RevisionScheme = {
  isMember: patternIsMember,
  next: patternNext,
  toOrdinal: (label, startPattern) => {
    if (!label || !startPattern || !patternIsMember(label, startPattern)) return null;
    let current: string | null = startPattern;
    for (let position = 0; position < PATTERN_MAX_POSITIONS && current; position += 1) {
      if (current === label) return position;
      current = patternNext(current);
    }
    return null;
  },
  fromOrdinal: (n, startPattern) => {
    if (!startPattern || detectPatternKind(startPattern) === "unknown" || n < 0) return null;
    let current: string | null = startPattern;
    for (let i = 0; i < n && current; i += 1) current = patternNext(current);
    return current;
  },
};

const CUSTOM_SINGLE_LETTER = /^[A-Z]$/;
const CUSTOM_BARE_NUMBER = /^\d{1,2}$/;
const CUSTOM_PREFIXED = /^([A-Z]+)-(\d{1,2})$/;

const PHASE1_SIZE = 26;
const NUMERIC_MAX = 50;
const PHASE2_SIZE = NUMERIC_MAX + 1;
const PHASE3_START = PHASE1_SIZE + PHASE2_SIZE;
const PREFIX_SUB_MAX = 50;

const bijectiveToIndex = (letters: string): number => {
  let index = 0;
  for (let i = 0; i < letters.length; i += 1) {
    index = index * 26 + (letters.charCodeAt(i) - 64);
  }
  return index;
};

const bijectiveFromIndex = (index: number): string => {
  let result = "";
  let n = index;
  while (n > 0) {
    n -= 1;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result;
};

const customLabelToOrdinal = (label: string): number | null => {
  const value = (label ?? "").trim().toUpperCase();
  if (!value) return null;

  if (CUSTOM_SINGLE_LETTER.test(value)) return value.charCodeAt(0) - 65;

  if (CUSTOM_BARE_NUMBER.test(value)) {
    const n = Number(value);
    if (String(n) === value && n >= 0 && n <= NUMERIC_MAX) return PHASE1_SIZE + n;
    return null;
  }

  const match = value.match(CUSTOM_PREFIXED);
  const [, prefix, subToken] = match ?? [];
  if (prefix && subToken) {
    const sub = Number(subToken);
    if (sub >= 1 && sub <= PREFIX_SUB_MAX) {
      const prefixIndex = bijectiveToIndex(prefix);
      return PHASE3_START + (prefixIndex - 1) * PREFIX_SUB_MAX + (sub - 1);
    }
  }
  return null;
};

const customOrdinalToLabel = (n: number): string | null => {
  if (n < 0 || !Number.isInteger(n)) return null;
  if (n < PHASE1_SIZE) return String.fromCharCode(65 + n);
  if (n < PHASE3_START) return String(n - PHASE1_SIZE);
  const offset = n - PHASE3_START;
  const prefixIndex = Math.floor(offset / PREFIX_SUB_MAX) + 1;
  const sub = (offset % PREFIX_SUB_MAX) + 1;
  return `${bijectiveFromIndex(prefixIndex)}-${String(sub).padStart(2, "0")}`;
};

const alphaNumericPrefixedScheme: RevisionScheme = {
  isMember: (value) => customLabelToOrdinal(value) !== null,
  next: (label) => {
    const ordinal = customLabelToOrdinal(label);
    return ordinal === null ? null : customOrdinalToLabel(ordinal + 1);
  },
  toOrdinal: (label) => customLabelToOrdinal(label),
  fromOrdinal: (n) => customOrdinalToLabel(n),
};

const SCHEMES: Record<RevisionSequenceType, RevisionScheme> = {
  PATTERN: patternScheme,
  ALPHA_NUMERIC_PREFIXED: alphaNumericPrefixedScheme,
};

const resolveScheme = (sequenceType?: RevisionSequenceType | null): RevisionScheme =>
  SCHEMES[sequenceType ?? DEFAULT_SEQUENCE_TYPE] ?? patternScheme;

export const isRevisionInSeries = (
  value: string,
  startPattern: string,
  sequenceType?: RevisionSequenceType | null,
): boolean => resolveScheme(sequenceType).isMember(value, startPattern);

export const incrementRevisionValue = (
  current: string,
  sequenceType?: RevisionSequenceType | null,
): string | null => resolveScheme(sequenceType).next(current);

export const getRevisionPosition = (
  label: string,
  startPattern: string,
  sequenceType?: RevisionSequenceType | null,
): number | null => resolveScheme(sequenceType).toOrdinal(label, startPattern);

export const previewPattern = (
  start: string,
  count = 6,
  sequenceType?: RevisionSequenceType | null,
): string[] => {
  const scheme = resolveScheme(sequenceType);
  const seed = scheme.fromOrdinal(0, start);
  if (!seed) return [];
  const values = [seed];
  let current: string | null = seed;
  for (let i = 1; i < count; i += 1) {
    current = scheme.next(current);
    if (!current) break;
    values.push(current);
  }
  return values;
};

export const canonicalizeRevisionLabel = (label: string, sequenceType?: RevisionSequenceType | null): string => {
  const trimmed = (label ?? "").trim();
  if (isCustomSequenceType(sequenceType)) {
    const ordinal = customLabelToOrdinal(trimmed);
    if (ordinal !== null) return customOrdinalToLabel(ordinal) ?? trimmed;
  }
  return trimmed;
};

export type RevisionSequenceRules = {
  allowSkipFirst?: boolean;
  allowSkipIntermediate?: boolean;
  allowAnyOrder?: boolean;
  noRepeatedRevisions?: boolean;
  revisionMandatory?: boolean;
};

export const DEFAULT_SEQUENCE_RULES: Required<RevisionSequenceRules> = {
  allowSkipFirst: false,
  allowSkipIntermediate: false,
  allowAnyOrder: false,
  noRepeatedRevisions: false,
  revisionMandatory: false,
};

export const getEffectiveSequenceRules = (series?: {
  defaultBehavior?: string | null;
  sequenceRules?: RevisionSequenceRules | null;
}): Required<RevisionSequenceRules> => {
  const stored = { ...DEFAULT_SEQUENCE_RULES, ...(series?.sequenceRules ?? {}) };
  if (series?.defaultBehavior === "ALWAYS_PREVIOUS") {
    return {
      allowSkipFirst: false,
      allowSkipIntermediate: false,
      allowAnyOrder: stored.allowAnyOrder,
      noRepeatedRevisions: false,
      revisionMandatory: stored.revisionMandatory,
    };
  }
  return stored;
};

export const hasAnySkipRule = (rules: RevisionSequenceRules): boolean =>
  !!rules.allowSkipFirst || !!rules.allowSkipIntermediate || !!rules.allowAnyOrder;

export type RevisionRuleViolation =
  | "NOT_IN_SERIES"
  | "MUST_BE_INITIAL"
  | "SEQUENTIAL_VIOLATION"
  | "MUST_ADVANCE"
  | "BACKWARDS_NOT_ALLOWED"
  | "REPEATED_REVISION"
  | "REVISION_REQUIRED";

export const REVISION_RULE_VIOLATION_MESSAGES: Record<RevisionRuleViolation, string> = {
  NOT_IN_SERIES: "Revision does not match the folder's Revision Series",
  MUST_BE_INITIAL: "Revision must start at the series's initial value",
  SEQUENTIAL_VIOLATION: "Revision must be same as or next after the current revision",
  MUST_ADVANCE: "Revision must be later in the series than the previous revision",
  BACKWARDS_NOT_ALLOWED: "Upload of earlier revision is not allowed",
  REPEATED_REVISION: "Revision is already assigned to another version of this file",
  REVISION_REQUIRED: "Metadata is not available in the Document Register, file cannot be uploaded",
};

export const validateRevisionAgainstRules = ({
  label,
  startPattern,
  previousLabel,
  existingLabels = [],
  rules,
  sequenceType,
}: {
  label: string;
  startPattern: string;
  previousLabel?: string | null;
  existingLabels?: string[];
  rules?: RevisionSequenceRules | null;
  sequenceType?: RevisionSequenceType | null;
}): RevisionRuleViolation | null => {
  const effective = { ...DEFAULT_SEQUENCE_RULES, ...(rules ?? {}) };
  const anyOrder = effective.allowAnyOrder;
  const skipFirst = anyOrder || effective.allowSkipFirst;
  const skipIntermediate = anyOrder || effective.allowSkipIntermediate;

  const position = getRevisionPosition(label, startPattern, sequenceType);
  if (position === null) return "NOT_IN_SERIES";

  const previousPosition = previousLabel ? getRevisionPosition(previousLabel, startPattern, sequenceType) : null;

  if (!anyOrder && previousPosition !== null && position < previousPosition) return "BACKWARDS_NOT_ALLOWED";

  if (effective.noRepeatedRevisions && existingLabels.includes(label)) return "REPEATED_REVISION";

  if (previousPosition === null) {
    if (!skipFirst && position !== 0) return "MUST_BE_INITIAL";
    return null;
  }

  if (anyOrder) return null;
  if (skipIntermediate) {
    if (position === previousPosition) return "MUST_ADVANCE";
    return null;
  }
  if (position > previousPosition + 1) return "SEQUENTIAL_VIOLATION";
  return null;
};
